import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../general/Icon";

// ---------------------------------------------------------------------------
// Demonstration content (kept separate from presentation logic)
// ---------------------------------------------------------------------------

const DEFAULTS = {
  title: "Stuck on a concept? Try a different approach.",
  description: "Explore the different ways StudyLens can help you learn.",
  activities: ["reading", "animated", "tracing", "parsons", "challenges"],
};

const READING = {
  heading: "What is a for loop?",
  paragraphs: [
    "A for loop repeats a block of code once for every item in a sequence.",
    "The loop variable takes the next value each time around.",
  ],
  code: 'for name in ["Ada", "Alan"]:\n    print("Hello", name)',
  note: "Python uses indentation to decide which lines belong to the loop.",
};

const ANIMATED = {
  lines: ["x = 2", "y = x * 3", "x = y + 1"],
  steps: [
    { line: -1, vars: {}, text: "Press Step to run the first line." },
    { line: 0, vars: { x: 2 }, text: "x is created and stores 2." },
    { line: 1, vars: { x: 2, y: 6 }, text: "x * 3 is 6, so y stores 6." },
    { line: 2, vars: { x: 7, y: 6 }, text: "y + 1 is 7, so x is updated to 7." },
  ],
};

const TRACING = {
  code: "total = 0\nfor i in range(3):\n    total += i\nprint(total)",
  output: "3",
  explanation: "i takes the values 0, 1 and 2, so total becomes 0 + 0 + 1 + 2 = 3.",
};

const PARSONS = {
  blocks: [
    { id: "init", text: "total = 0", indent: 0 },
    { id: "for", text: "for n in [1, 2, 3]:", indent: 0 },
    { id: "add", text: "total += n", indent: 1 },
    { id: "print", text: "print(total)", indent: 0 },
  ],
  initialOrder: ["init", "for", "print", "add"],
  solution: ["init", "for", "add", "print"],
};

const CHALLENGE = {
  example: {
    title: "Example: Displaying a Music Library",
    description:
      "A music app stores song titles and artists in a text file. Read the file and display each song, handling the case where the file is missing.",
    lines: [
      {
        text: 'with open("music.csv", "r") as file:',
        explain: 'Opens music.csv for reading ("r"). The with block closes the file automatically.',
      },
      {
        text: "  for row in file:",
        explain: "Repeats the indented lines once for every line in the file.",
      },
      {
        text: '    title, artist = row.strip().split(",")',
        explain: "Removes the line break, then splits the line at the comma into two variables.",
      },
      { text: '    print(f"{title} - {artist}")' }
    ],
  },
  challenge: {
    title: "Challenge: Displaying a Book List",
    description:
      'A library app stores book titles and authors in a text file. Pick the tile that opens books.csv for reading. Hint: in the example, "r" meant read.',
    lines: [
      { gap: true, indent: "" },
      { text: "for row in file:" },
      { text: '  title, author = row.strip().split(",")' },
      { text: '  print(f"{title} by {author}")' },
    ],
    tiles: [
      { id: "write", text: 'with open("books.csv", "w") as file:', correct: false, why: 'Mode "w" is for writing and would erase the file instead of reading it.' },
      { id: "read", text: 'with open("books.csv", "r") as file:', correct: true },
      { id: "noWith", text: 'file = open("books.csv", "r")', correct: false, why: "This opens the file, but the loop below is written for a with block like the one in the example. Look for the tile that matches the example." },
    ],
  },
};

// ---------------------------------------------------------------------------
// Activity families. Each maps to an existing StudyLens activity type so the
// original icon from <Icon /> is reused.
// ---------------------------------------------------------------------------

const FAMILIES = {
  reading: {
    label: "Read",
    name: "Reading materials",
    iconType: "learning_material_paws_reading",
    summary: "Concise explanations of the important topics, written for beginners.",
    teaser: "Got 30 seconds? Learn a new concept.",
    task: "Read the short explanation, then try the examples in your own words.",
    Demo: ReadingDemo,
  },
  animated: {
    label: "Watch",
    name: "Animated examples",
    iconType: "learning_material_paws_animated",
    summary: "See what code does during execution, one line at a time.",
    teaser: "Ever wondered what the computer does with your code?",
    task: "Step through the program and watch the variables change.",
    Demo: AnimatedDemo,
  },
  tracing: {
    label: "Trace",
    name: "Tracing problems",
    iconType: "learning_material_paws_tracing",
    summary: "Predict the output of a program to check you can follow the code.",
    teaser: "Can you predict the output?",
    task: "Work out the output in your head, then reveal the answer.",
    Demo: TracingDemo,
  },
  parsons: {
    label: "Build",
    name: "Parsons problems",
    iconType: "learning_material_paws_parsons",
    summary: "Assemble a working program from shuffled blocks of code.",
    teaser: "Can you put the scrambled code back in order?",
    task: "Use the arrows to put the blocks in order, then press Check.",
    Demo: ParsonsDemo,
  },
  challenges: {
    label: "Solve",
    name: "Challenges",
    iconType: "learning_material_paws_challenges",
    summary: "Study a worked example, then complete a similar program yourself.",
    teaser: "Think you've got it? Prove it!",
    task: "Click the ? marks to understand the example, then press Challenge me!",
    Demo: ChallengeDemo,
  },
};

// Database activity types and prefixes that belong to each family.
const FAMILY_ALIASES = {
  reading: "reading",
  book: "reading",
  animated: "animated",
  tracing: "tracing",
  parsons: "parsons",
  challenges: "challenges",
  challenge: "challenges",
};

const PREFIXES = [
  "learning_material_paws_",
  "learning_material_splice_",
  "learning_material_",
  "learning_activity_",
];

const stripPrefix = (id) => {
  const prefix = PREFIXES.find((p) => id.startsWith(p));
  return prefix ? id.slice(prefix.length) : id;
};

const titleCase = (s) =>
  s
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Turns configured entries (string or object) into renderable items.
// Unknown identifiers get a generic item instead of failing.
const resolveActivities = (configured) => {
  const list = Array.isArray(configured) && configured.length > 0 ? configured : DEFAULTS.activities;
  const seen = new Set();
  const items = [];

  list.forEach((entry) => {
    const raw = typeof entry === "string" ? entry : entry?.type;
    if (typeof raw !== "string" || !raw.trim()) return;
    const id = raw.trim();
    if (seen.has(id)) return;
    seen.add(id);

    const override = typeof entry === "object" && entry ? entry : {};
    const base = stripPrefix(id);
    const familyKey = FAMILY_ALIASES[base];
    const family = familyKey ? FAMILIES[familyKey] : null;

    if (family) {
      items.push({
        id,
        label: override.label || family.label,
        name: override.name || family.name,
        iconType: family.iconType,
        summary: override.description || family.summary,
        teaser: override.teaser || family.teaser,
        task: family.task,
        Demo: family.Demo,
      });
    } else {
      const name = override.name || titleCase(base);
      items.push({
        id,
        label: override.label || name,
        name,
        iconType: id.startsWith("learning_") ? id.replace("learning_activity_", "learning_material_") : "learning_material_paws_reading",
        summary: override.description || "Another way to practise and learn in this course.",
        teaser: override.teaser || "Curious what this one does?",
        task: "You will find this activity type in your course.",
        Demo: GenericDemo,
      });
    }
  });

  return items;
};

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

const Code = ({ children, highlightLine = -1 }) => (
  <pre className="m-0 overflow-x-auto rounded-lg bg-slate-900 px-4 py-3 font-mono text-sm leading-6 text-slate-100">
    {String(children)
      .split("\n")
      .map((line, i) => (
        <div
          key={i}
          className={i === highlightLine ? "-mx-4 bg-accent/30 px-4 text-white" : undefined}
        >
          {line || " "}
        </div>
      ))}
  </pre>
);

const DemoButton = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-black shadow-default duration-200 hover:bg-accentdark disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
  >
    {children}
  </button>
);

const SecondaryButton = ({ children, ...props }) => (
  <button
    type="button"
    {...props}
    className="rounded-lg border border-black/20 bg-white px-4 py-2 text-sm font-semibold text-black duration-200 hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Demonstrations (local state only: nothing is submitted or graded)
// ---------------------------------------------------------------------------

function ReadingDemo({ onDone }) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="m-0 font-MS text-lg font-bold">{READING.heading}</h3>
      {READING.paragraphs.map((p) => (
        <p key={p} className="m-0 text-sm leading-relaxed text-slate-700">
          {p}
        </p>
      ))}
      <Code>{READING.code}</Code>
      <p className="m-0 rounded-lg border border-accent bg-accent/20 px-3 py-2 text-sm">
        <strong>Tip:</strong> {READING.note}
      </p>
      <div>
        <DemoButton onClick={onDone}>Got it</DemoButton>
      </div>
    </div>
  );
}

function AnimatedDemo({ onDone }) {
  const [step, setStep] = useState(0);
  const current = ANIMATED.steps[step];
  const last = step === ANIMATED.steps.length - 1;

  const advance = () => {
    setStep(step + 1);
    if (step + 1 === ANIMATED.steps.length - 1) onDone?.();
  };

  return (
    <div className="flex flex-col gap-3">
      <Code highlightLine={current.line}>{ANIMATED.lines.join("\n")}</Code>
      <div className="rounded-lg border border-black/10 bg-white px-4 py-2 text-sm">
        <div className="font-semibold">Variables</div>
        {Object.keys(current.vars).length === 0 ? (
          <div className="text-slate-500">None yet</div>
        ) : (
          <div className="font-mono">
            {Object.entries(current.vars).map(([k, v]) => `${k} = ${v}`).join("   ")}
          </div>
        )}
      </div>
      <p className="m-0 text-sm" aria-live="polite">
        <strong>
          Step {step} of {ANIMATED.steps.length - 1}:
        </strong>{" "}
        {current.text}
      </p>
      <div className="flex gap-2">
        <DemoButton onClick={advance} disabled={last}>
          Step forward
        </DemoButton>
        <SecondaryButton onClick={() => setStep(0)} disabled={step === 0}>
          Restart
        </SecondaryButton>
      </div>
    </div>
  );
}

function TracingDemo({ onDone }) {
  const [revealed, setRevealed] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 text-sm font-semibold">What does this program print?</p>
      <p className="m-0 rounded-lg bg-white px-3 py-2 text-sm">
        <strong>Hint:</strong> <code className="font-mono">range(3)</code> gives 0, 1 and 2. Add
        them up one at a time.
      </p>
      <Code>{TRACING.code}</Code>
      {revealed ? (
        <div className="rounded-lg border border-black/10 bg-white px-4 py-3 text-sm" aria-live="polite">
          <div>
            Output: <code className="font-mono font-bold">{TRACING.output}</code>
          </div>
          <div className="mt-1 text-slate-700">{TRACING.explanation}</div>
        </div>
      ) : (
        <div>
          <DemoButton
            onClick={() => {
              setRevealed(true);
              onDone?.();
            }}
          >
            Reveal output
          </DemoButton>
        </div>
      )}
      {revealed && (
        <div>
          <SecondaryButton onClick={() => setRevealed(false)}>Hide again</SecondaryButton>
        </div>
      )}
    </div>
  );
}

function ParsonsDemo({ onDone }) {
  const [order, setOrder] = useState(PARSONS.initialOrder);
  const [result, setResult] = useState(null);
  const blocksById = useMemo(
    () => Object.fromEntries(PARSONS.blocks.map((b) => [b.id, b])),
    []
  );

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setResult(null);
  };

  const check = () => {
    const correct = order.filter((id, i) => id === PARSONS.solution[i]).length;
    setResult({ correct, total: order.length });
    if (correct === order.length) onDone?.();
  };

  const solved = result && result.correct === result.total;

  return (
    <div className="flex flex-col gap-3">
      <p className="m-0 rounded-lg bg-white px-3 py-2 text-sm">
        <strong>Goal:</strong> add up 1, 2 and 3 and print the total. <strong>Hint:</strong> start
        with the total, then the loop, then the line inside the loop, and print last. Only one
        block is out of place.
      </p>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {order.map((id, i) => {
          const block = blocksById[id];
          return (
            <li
              key={id}
              className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-2 py-1"
            >
              <code
                className="flex-1 font-mono text-sm"
                style={{ paddingLeft: `${block.indent * 1.5}rem` }}
              >
                {block.text}
              </code>
              {result && (
                <span className="text-xs font-semibold">
                  {id === PARSONS.solution[i] ? "✓ right place" : "← try moving this"}
                </span>
              )}
              <button
                type="button"
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label={`Move "${block.text}" up`}
                className="h-8 w-8 rounded-md border border-black/20 bg-secondary disabled:opacity-30 hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
              >
                ↑
              </button>
              <button
                type="button"
                onClick={() => move(i, 1)}
                disabled={i === order.length - 1}
                aria-label={`Move "${block.text}" down`}
                className="h-8 w-8 rounded-md border border-black/20 bg-secondary disabled:opacity-30 hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
              >
                ↓
              </button>
            </li>
          );
        })}
      </ol>
      <div className="flex items-center gap-3">
        <DemoButton onClick={check}>Check</DemoButton>
        <p className="m-0 text-sm" aria-live="polite">
          {result &&
            (solved
              ? "Correct! The loop adds each number to total, then prints the result."
              : `Almost! ${result.correct} of ${result.total} blocks are in the right place. Look for the blocks marked "try moving this". The line inside the loop must come right after the for line.`)}
        </p>
      </div>
    </div>
  );
}

const NumberedCode = ({ children }) => (
  <div className="overflow-x-auto rounded-lg bg-slate-900 py-2 font-mono text-sm leading-6 text-slate-100">
    {children}
  </div>
);

const CodeRow = ({ n, children, marker }) => (
  <div className="flex items-start px-2">
    <span className="flex w-9 shrink-0 items-center justify-end gap-1 pr-2 text-slate-400 select-none">
      {marker}
      {n}
    </span>
    <span className="whitespace-pre">{children}</span>
  </div>
);

// Title bar with an optional action button beside it (PCEX style):
// pass `title` and `action` ({ label, onClick, disabled }) to configure it.
const DemoHeader = ({ title, color, action, actionSide = "right" }) => {
  const button = action && (
    <button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      className={`shrink-0 rounded-lg px-3 py-2 text-sm font-semibold text-white shadow-default duration-200 hover:brightness-110 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${color}`}
    >
      {action.label}
    </button>
  );
  return (
    <div className="flex items-stretch gap-2">
      {actionSide === "left" && button}
      <div className={`flex flex-1 items-center rounded-lg px-3 py-2 text-sm font-semibold text-white ${color}`}>
        {title}
      </div>
      {actionSide === "right" && button}
    </div>
  );
};

function ChallengeDemo({ onDone }) {
  const { example, challenge } = CHALLENGE;
  const [mode, setMode] = useState("example");
  const [openLines, setOpenLines] = useState([]);
  const [placedId, setPlacedId] = useState(null);
  const [result, setResult] = useState(null);

  const placed = challenge.tiles.find((t) => t.id === placedId);
  const toggleLine = (i) =>
    setOpenLines((o) => (o.includes(i) ? o.filter((x) => x !== i) : [...o, i]));

  if (mode === "example") {
    return (
      <div className="flex flex-col gap-3">
        <DemoHeader
          title={example.title}
          color="bg-blue-500"
          action={{ label: "Challenge me! →", onClick: () => setMode("challenge") }}
        />

        <p className="m-0 text-sm text-slate-700">{example.description}</p>
        <NumberedCode>
          {example.lines.map((line, i) => (
            <div key={i}>
              <CodeRow
                n={i + 1}
                marker={
                  line.explain && (
                    <button
                      type="button"
                      onClick={() => toggleLine(i)}
                      aria-expanded={openLines.includes(i)}
                      aria-label={`Explain line ${i + 1}`}
                      className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-red-500 bg-white text-xs font-bold text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      ?
                    </button>
                  )
                }
              >
                {line.text}
              </CodeRow>
              {line.explain && openLines.includes(i) && (
                <div className="mx-2 my-1 ml-11 rounded-md bg-white px-3 py-1 font-sans text-xs text-slate-800">
                  {line.explain}
                </div>
              )}
            </div>
          ))}
        </NumberedCode>

      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <DemoHeader
        title={challenge.title}
        color="bg-orange-600"
        actionSide="left"
        action={{
          label: "← Back",
          onClick: () => {
            setMode("example");
            setPlacedId(null);
            setResult(null);
          },
        }}
      />
      <p className="m-0 text-sm text-slate-700">{challenge.description}</p>
      <NumberedCode>
        {challenge.lines.map((line, i) =>
          line.gap ? (
            <CodeRow key={i} n={i + 1}>
              {line.indent}
              {placed ? (
                <button
                  type="button"
                  onClick={() => {
                    setPlacedId(null);
                    setResult(null);
                  }}
                  aria-label={`Remove tile: ${placed.text}`}
                  className="rounded border border-dashed border-sky-400 px-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  {placed.text}
                </button>
              ) : (
                <span className="rounded border border-dashed border-sky-400 px-2 text-sky-300">
                  choose a tile below
                </span>
              )}
            </CodeRow>
          ) : (
            <CodeRow key={i} n={i + 1}>
              {line.text}
            </CodeRow>
          )
        )}
      </NumberedCode>
      <div className="flex flex-col gap-2">
        <p className="m-0 text-xs font-semibold text-slate-600">Select a tile to place it in the gap:</p>
        {challenge.tiles.map((t) => (
          <button
            key={t.id}
            type="button"
            disabled={t.id === placedId}
            onClick={() => {
              setPlacedId(t.id);
              setResult(null);
            }}
            className="rounded-lg border-2 border-dashed border-sky-400 bg-white px-3 py-1 text-left font-mono text-xs duration-200 hover:bg-secondary disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          >
            {t.text}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DemoButton
          disabled={!placed}
          onClick={() => {
            setResult(placed.correct ? "ok" : "bad");
            if (placed.correct) onDone?.();
          }}
        >
          Check
        </DemoButton>
        <p className="m-0 text-sm" aria-live="polite">
          {result === "ok" && "Correct! Reading mode with a with block opens the file and closes it automatically."}
          {result === "bad" && `Not quite. ${placed.why}`}
        </p>
      </div>
    </div>
  );
}

function GenericDemo({ item, onDone }) {
  useEffect(() => {
    onDone?.();
  }, []);
  return (
    <p className="m-0 text-sm text-slate-700">
      {item.summary} A preview is not available for this activity type, but you will see it listed
      among your course activities.
    </p>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const ActivityOverview = ({ slide, data: dataProp }) => {
  let data = dataProp ?? slide?.datajson ?? {};
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  if (!data || typeof data !== "object" || Array.isArray(data)) data = {};

  const text = (v, fallback) => (typeof v === "string" && v.trim() ? v : fallback);
  const title = text(data.title, DEFAULTS.title);
  const description = text(data.description, DEFAULTS.description);
  const highlight = text(data.highlight, null);

  const items = useMemo(() => resolveActivities(data.activities), [JSON.stringify(data.activities)]);
  const [selectedId, setSelectedId] = useState(null);
  const [doneIds, setDoneIds] = useState([]);
  const tabRefs = useRef([]);

  if (items.length === 0) return null;

  const selectedIndex = Math.max(0, items.findIndex((i) => i.id === selectedId));
  const selected = items[selectedIndex];
  const Demo = selected.Demo;
  const markDone = (id) => setDoneIds((d) => (d.includes(id) ? d : [...d, id]));
  const nextItem = items.find((it, i) => i > selectedIndex && !doneIds.includes(it.id))
    || items.find((it) => it.id !== selected.id && !doneIds.includes(it.id));
  const showStartHint = selectedId === null && doneIds.length === 0;

  const onKeyDown = (e) => {
    const keys = { ArrowRight: 1, ArrowLeft: -1 };
    let next = null;
    if (e.key in keys) next = (selectedIndex + keys[e.key] + items.length) % items.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    if (next == null) return;
    e.preventDefault();
    setSelectedId(items[next].id);
    tabRefs.current[next]?.focus();
  };


  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-4 sm:px-8">
      <header className="text-center pt-6">
        <h1 className="m-0 font-MS text-2xl font-extrabold leading-tight text-slate-900 lg:text-3xl">
          {title}
        </h1>
        <p className="m-0 mt-1 text-base text-slate-600">
          {description}
        </p>
      </header>

      {/* Main layout: sidebar + activity content */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-[190px_minmax(0,1fr)]">

        {/* LEFT SIDEBAR */}
        <div
          role="tablist"
          aria-label="Activity types"
          aria-orientation="vertical"
          onKeyDown={onKeyDown}
          className="flex flex-col gap-2"
        >
          {items.map((item, i) => {
            const active = i === selectedIndex;

            return (
              <button
                key={item.id}
                ref={(el) => (tabRefs.current[i] = el)}
                type="button"
                role="tab"
                title={item.teaser}
                id={`activity-tab-${item.id}`}
                aria-selected={active}
                aria-controls="activity-panel"
                tabIndex={active ? 0 : -1}
                onClick={() => setSelectedId(item.id)}
                className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm font-semibold duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${active
                    ? "border-black bg-accent shadow-default"
                    : "border-black/10 bg-white hover:border-accent"
                  }`}
              >
                <Icon
                  type={item.iconType}
                  size={26}
                  color="currentColor"
                  aria-hidden="true"
                />

                <span>{item.label}</span>

                {active && (
                  <span className="sr-only">(selected)</span>
                )}
                {doneIds.includes(item.id) && (
                  <span className="text-xs font-normal text-green-800">✓ Done</span>
                )}
                {showStartHint && i === 0 && (
                  <span className="text-xs font-bold text-black motion-safe:animate-pulse">▲ Start here</span>
                )}
              </button>
            );
          })}
        </div>

        {/* RIGHT CONTENT PANEL */}
        <section
          key={selected.id}
          role="tabpanel"
          id="activity-panel"
          aria-labelledby={`activity-tab-${selected.id}`}
          className="grid min-w-0 grid-cols-1 gap-5 rounded-2xl border border-black/10 bg-secondary p-5 motion-safe:animate-fadeIn lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
        >
          {/* Explanation */}
          <div className="flex flex-col gap-3">
            <h2 className="m-0 flex items-center gap-2 font-MS text-xl font-bold">
              <Icon
                type={selected.iconType}
                size={24}
                color="currentColor"
                aria-hidden="true"
              />
              {selected.name}
            </h2>

            {selected.teaser && (
              <p className="m-0 text-lg font-bold italic text-slate-900">
                {selected.teaser}
              </p>
            )}

            <p className="m-0 text-base text-slate-700">
              {selected.summary}
            </p>

            <p className="m-0 rounded-lg bg-white px-3 py-2 text-sm">
              <strong>What you do:</strong> {selected.task}
            </p>

            <p className="m-0 text-xs text-slate-500">
              This is a preview. Nothing you do here is saved or graded.
            </p>
          </div>

          {/* Interactive demonstration */}
          <div className="min-w-0">
            <Demo item={selected} onDone={() => markDone(selected.id)} />
          {doneIds.includes(selected.id) && nextItem && (
            <div
              className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-accent bg-accent/20 px-3 py-2 text-sm motion-safe:animate-fadeIn"
              aria-live="polite"
            >
              <span>
                <strong>Nice work!</strong> Up next: {nextItem.name}
              </span>
              <DemoButton onClick={() => setSelectedId(nextItem.id)}>Next: {nextItem.label} →</DemoButton>
            </div>
          )}
          </div>
        </section>
      </div>

      {/* Optional highlight */}
      {highlight && (
        <div className="mx-auto w-full max-w-2xl rounded-xl border border-amber-200 bg-amber-50 px-6 py-3 text-center text-base font-semibold text-amber-950">
          {highlight}
        </div>
      )}
    </div>
  );

};

export default ActivityOverview;
